import { useState, useEffect } from 'react'
import OnboardingTooltip from './ui/OnboardingTooltip'

const TOUR_STEPS = [
  {
    target: 'ai-panel',
    title: 'AI Coding Assistant',
    description: 'Type what you want to create and AI writes the code! Try: "Make a bouncing ball" or "Create particles that follow the mouse"',
    position: 'left'
  },
  {
    target: 'model-selector',
    title: 'Choose Your AI Model',
    description: 'Pick from OpenAI (GPT-5.1), Claude, Gemini, or our custom p5flash model trained specifically for creative coding.',
    position: 'bottom'
  },
  {
    target: 'turbo-mode',
    title: 'Turbo Mode ⚡',
    description: 'Auto-applies code, runs it, and fixes errors automatically. Toggle OFF if you prefer to review code before applying.',
    position: 'top'
  },
  {
    target: 'ai-input',
    title: 'How to Apply Code',
    description: 'When Turbo is OFF: AI generates code → Click "Apply" button on code blocks → Click "Run" to see it. With Turbo ON, this happens automatically!',
    position: 'top'
  },
  {
    target: 'run-button',
    title: 'Run Your Sketch',
    description: 'Execute your code and see it live. Shortcut: Ctrl+Enter. Code also auto-runs 350ms after you stop typing.',
    position: 'bottom'
  },
  {
    target: 'preview',
    title: 'Live Preview',
    description: 'Your sketch runs here in real-time. Click ↗️ to pop out to a new window or ⛶ for fullscreen mode.',
    position: 'left'
  },
  {
    target: 'editor',
    title: 'Code Editor',
    description: 'View and edit your p5.js code with syntax highlighting. Supports multiple files (sketch.js, style.css, index.html).',
    position: 'right'
  },
  {
    target: 'console',
    title: 'Debug Console',
    description: 'See errors, warnings, and console.log() output. Red = error, Yellow = warning, Blue = info.',
    position: 'top'
  },
  {
    target: 'templates',
    title: 'Starter Templates',
    description: 'Quick-start with particles, games, audio visualizers, webcam sketches, and more! Great for learning.',
    position: 'bottom'
  },
  {
    target: 'publish',
    title: 'Share Your Creation',
    description: 'Publish to the public gallery with a shareable URL. You can include your AI chat history so others can see how you built it!',
    position: 'bottom'
  },
  {
    target: 'help-button',
    title: 'Get Help Anytime',
    description: 'Click here to restart this tour. Also check /docs for the full user guide and /tutorials for p5.js function examples.',
    position: 'bottom'
  }
]

const STORAGE_KEY = 'p5ai_onboarding_complete'

export default function OnboardingTour({ forceShow = false, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (forceShow) {
      setIsVisible(true)
      setCurrentStep(0)
      return
    }

    // Check if first visit
    try {
      const completed = localStorage.getItem(STORAGE_KEY)
      if (!completed) {
        // Delay start to let UI load
        const timer = setTimeout(() => {
          setIsVisible(true)
        }, 2000)
        return () => clearTimeout(timer)
      }
    } catch (e) {
      // localStorage blocked - don't show tour
    }
  }, [forceShow])

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1)
    } else {
      completeTour()
    }
  }

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }

  const handleSkip = () => {
    completeTour()
  }

  const completeTour = () => {
    setIsVisible(false)
    try {
      localStorage.setItem(STORAGE_KEY, 'true')
    } catch (e) {
      // localStorage blocked
    }
    onComplete?.()
  }

  if (!isVisible) return null

  const step = TOUR_STEPS[currentStep]

  return (
    <OnboardingTooltip
      target={step.target}
      title={step.title}
      description={step.description}
      position={step.position}
      step={currentStep + 1}
      totalSteps={TOUR_STEPS.length}
      onNext={handleNext}
      onPrev={handlePrev}
      onSkip={handleSkip}
    />
  )
}

// Export function to trigger tour from anywhere
export function resetOnboardingTour() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (e) {}
}
